import { useMediaPack } from '@/hooks/use-media-pack';
import { useWorkflowByDefnIdForRecipient } from '@/hooks/use-workflows';
import {
    getProgramId,
    getProgramWorkflowIdFromMetadata,
} from '@/utils/programs';
import PageTitle from '@rhg/components/page-title';
import { Program as ProgramComponent } from '@rhg/components/program';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useProgram } from '@rhg/hooks/use-program';
import programMessages from '@rhg/lang/messages/program';
import withAuth from '@rhg/libs/auth0/with-auth';
import { logger } from '@rhg/libs/logger';
import { Box } from '@rhg/libs/material';
import { getSingleStringFromQuery, routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

function ProgramForId({
    programId,
    isLoading,
}: {
    programId?: string;
    isLoading?: boolean;
}) {
    const { formatMessage } = useIntl();
    const { query, replace, isReady } = useRouter();
    const packClassQuery =
        getSingleStringFromQuery(query, 'pack_class') ??
        getSingleStringFromQuery(query, 'packClass');
    const showOverview =
        getSingleStringFromQuery(query, 'show_overview') ??
        getSingleStringFromQuery(query, 'showOverview');

    const { activeCircleId } = useActiveCircle();

    const { program, isFetched: isProgramFetched } = useProgram(
        programId,
        activeCircleId
    );

    // Fetch the workflow before rendering ProgramComponent so that we know
    // if this is an in-progress/completed clinical program or not
    // (controls initial render state, like the default tab selection)
    const workflowId = getProgramWorkflowIdFromMetadata(program);
    const { workflow, isFetched: isWorkflowFetched } =
        useWorkflowByDefnIdForRecipient({
            workflowDefnId: workflowId ?? '',
            circleId: activeCircleId,
        });

    /**
     * We know that the program will never fetch:
     * if the router is ready (so we have all available query params)
     * and we're not loading prerequisite data (i.e., the clinical pack)
     * *and* we still have no program ID.
     * In that case, we can redirect to 404
     */
    const wontFetch = isReady && !isLoading && !programId;
    const isFetched = isProgramFetched && isWorkflowFetched;
    useEffect(() => {
        if ((isFetched || wontFetch) && !program) {
            replace(routes.discover).catch((err: unknown) =>
                logger.error(
                    'Program page: Failed to navigate to discover',
                    {},
                    err as Error
                )
            );
        }
    }, [isFetched, wontFetch, program, replace]);

    return (
        <Box>
            <PageTitle
                title={
                    program?.title ??
                    formatMessage(programMessages['program.title'])
                }
                showSrOnlyH1={!isFetched}
            />
            {!isFetched || !program ? (
                <FullWidthLinearProgress />
            ) : (
                <ProgramComponent
                    packClass={packClassQuery}
                    program={program}
                    workflow={workflow}
                    showOverview={!!showOverview}
                />
            )}
        </Box>
    );
}

function ProgramForPackClass({ packClass }: { packClass?: string }) {
    const { packClass: pack, isFetched } = useMediaPack(packClass);

    // This component is used when we *only* have a pack class, not a program ID.
    // In that case, only allow "clinical packs": packs with a single clinical program.
    const programMetadata = pack?.items?.find(
        (item) => item.type === 'program'
    );
    const isClinicalPack = Boolean(
        pack && pack.items.length === 1 && programMetadata?.isClinical
    );
    const programId =
        programMetadata && isClinicalPack
            ? getProgramId(programMetadata)
            : undefined;

    return <ProgramForId programId={programId} isLoading={!isFetched} />;
}

export function Program() {
    const { query } = useRouter();
    const packClassQuery =
        getSingleStringFromQuery(query, 'pack_class') ??
        getSingleStringFromQuery(query, 'packClass');
    const programIdQuery =
        getSingleStringFromQuery(query, 'program_id') ??
        getSingleStringFromQuery(query, 'programId');

    if (packClassQuery && !programIdQuery) {
        return <ProgramForPackClass packClass={packClassQuery} />;
    }
    return <ProgramForId programId={programIdQuery} />;
}

export default withAuth(Program);
